import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface GenerationResult {
  id: number;
  job_id: number;
  result_type: string;
  file_path: string;
  file_size: number;
  metadata: any;
  thumbnail_path: string;
  created_at: string;
  status: string;
  folder_path: string | null;
  user_tags: string[];
  job_name: string;
  workflow_name: string;
  seed_used: number;
}

interface UserFolder {
  id: number;
  user_id: number;
  name: string;
  hotkey: string;
  color: string;
  created_at: string;
}

interface ImageLightboxProps {
  images: GenerationResult[];
  currentIndex: number;
  onClose: () => void;
  onImageChange: (newIndex: number) => void;
  onArchive: (imageId: number) => void;
  onImageUpdate: (imageId: number, updates: Partial<GenerationResult>) => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  currentIndex,
  onClose,
  onImageChange,
  onArchive,
  onImageUpdate
}) => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [showFolderHint, setShowFolderHint] = useState(false);
  
  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPanPosition, setLastPanPosition] = useState({ x: 0, y: 0 });
  
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentImage = images[currentIndex];

  // Helper to detect if file is a video (handles URLs with query parameters)
  const isVideo = (url: string): boolean => {
    return /\.(mp4|webm|mov|avi|mkv|flv|wmv|m4v)(\?|$)/i.test(url);
  };

  // Reset zoom/pan when image changes
  useEffect(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
    setIsDragging(false);
  }, [currentIndex]);

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/folders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFolders(response.data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  };

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      onImageChange(currentIndex - 1);
    }
  }, [currentIndex, onImageChange]);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onImageChange(currentIndex + 1);
    }
  }, [currentIndex, images.length, onImageChange]);

  const handleArchive = useCallback(async () => {
    if (!currentImage) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/gallery/${currentImage.id}/archive`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      onArchive(currentImage.id);

      // Auto-advance to next image after archiving
      if (currentIndex < images.length - 1) {
        onImageChange(currentIndex + 1);
      } else if (currentIndex > 0) {
        onImageChange(currentIndex - 1);
      } else {
        // Last image, close lightbox
        onClose();
      }
    } catch (error) {
      console.error('Failed to archive image:', error);
    }
  }, [currentImage, currentIndex, images.length, onArchive, onImageChange, onClose]);

  const moveToFolder = useCallback(async (folder: UserFolder) => {
    if (!currentImage) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/gallery/${currentImage.id}/folder`, {
        folder_path: folder.name
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update the image in the parent component
      onImageUpdate(currentImage.id, { folder_path: folder.name });

      // Show brief feedback
      setShowFolderHint(true);
      setTimeout(() => setShowFolderHint(false), 1500);

      // Auto-advance to next image
      if (currentIndex < images.length - 1) {
        onImageChange(currentIndex + 1);
      } else if (currentIndex > 0) {
        onImageChange(currentIndex - 1);
      }
    } catch (error) {
      console.error('Failed to move to folder:', error);
    }
  }, [currentImage, currentIndex, images.length, onImageChange, onImageUpdate]);

  const removeFromFolder = useCallback(async () => {
    if (!currentImage) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/gallery/${currentImage.id}/folder`, {
        folder_path: ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Update the image in the parent component
      onImageUpdate(currentImage.id, { folder_path: null });

      // Show brief feedback
      setShowFolderHint(true);
      setTimeout(() => setShowFolderHint(false), 1500);

      // Auto-advance to next image
      if (currentIndex < images.length - 1) {
        onImageChange(currentIndex + 1);
      } else if (currentIndex > 0) {
        onImageChange(currentIndex - 1);
      }
    } catch (error) {
      console.error('Failed to remove from folder:', error);
    }
  }, [currentImage, currentIndex, images.length, onImageChange, onImageUpdate]);

  // Zoom functionality
  const handleWheel = useCallback((event: React.WheelEvent) => {
    if (!imageRef.current || !containerRef.current) return;
    
    event.preventDefault();
    
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    // Get image bounds
    const imageRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Calculate mouse position relative to image center
    const imageCenterX = (imageRect.left + imageRect.width / 2) - containerRect.left;
    const imageCenterY = (imageRect.top + imageRect.height / 2) - containerRect.top;
    
    // Mouse position relative to image center
    const offsetX = mouseX - imageCenterX;
    const offsetY = mouseY - imageCenterY;
    
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(scale * delta, 0.5), 10);
    
    if (newScale !== scale) {
      // Calculate new translation to keep zoom centered on mouse
      const scaleDiff = newScale - scale;
      const newTranslateX = translateX - (offsetX * scaleDiff);
      const newTranslateY = translateY - (offsetY * scaleDiff);
      
      setScale(newScale);
      setTranslateX(newTranslateX);
      setTranslateY(newTranslateY);
    }
  }, [scale, translateX, translateY]);

  // Pan functionality
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (scale <= 1) return; // Only allow panning when zoomed in
    
    event.preventDefault();
    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
    setLastPanPosition({ x: translateX, y: translateY });
  }, [scale, translateX, translateY]);

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isDragging || scale <= 1) return;
    
    const deltaX = event.clientX - dragStart.x;
    const deltaY = event.clientY - dragStart.y;
    
    setTranslateX(lastPanPosition.x + deltaX);
    setTranslateY(lastPanPosition.y + deltaY);
  }, [isDragging, dragStart, lastPanPosition, scale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Reset zoom
  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
  }, []);

  // Double-click to reset zoom
  const handleDoubleClick = useCallback(() => {
    resetZoom();
  }, [resetZoom]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase();

    switch (key) {
      case 'escape':
        onClose();
        break;
      case 'arrowleft':
        event.preventDefault();
        goToPrevious();
        break;
      case 'arrowright':
        event.preventDefault();
        goToNext();
        break;
      case 'delete':
      case 'backspace':
        event.preventDefault();
        handleArchive();
        break;
      case ' ':
        event.preventDefault();
        // TODO: Toggle favorite (Phase 3)
        break;
      case '0':
        event.preventDefault();
        resetZoom();
        break;
      case 'u':
        event.preventDefault();
        removeFromFolder();
        break;
      default:
        // Check for folder hotkeys (1-9, a-z)
        if (/^[1-9a-z]$/.test(key)) {
          event.preventDefault();
          const folder = folders.find(f => f.hotkey.toLowerCase() === key);
          if (folder) {
            moveToFolder(folder);
          }
        }
        break;
    }
  }, [onClose, goToPrevious, goToNext, handleArchive, folders, moveToFolder, resetZoom, removeFromFolder]);

  // Handle clicks on the lightbox background (empty space)
  const handleBackgroundClick = useCallback((event: React.MouseEvent) => {
    // Only close if clicking directly on the background container, not on child elements
    if (event.target === event.currentTarget) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Prevent background scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!currentImage) return null;

  // Get folder mappings for display
  const folderMappings = folders.reduce((acc, folder) => {
    if (folder.hotkey) {
      acc[folder.hotkey.toLowerCase()] = folder;
    }
    return acc;
  }, {} as Record<string, UserFolder>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50" onClick={handleBackgroundClick}>
      {/* Main image container */}
      <div 
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center p-4" 
        onClick={handleBackgroundClick}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'default' }}
      >
        {isVideo(currentImage.file_path) ? (
          <video
            ref={(el) => {
              if (el) {
                // Add click to play/pause functionality
                el.onclick = (e) => {
                  e.stopPropagation();
                  if (el.paused) {
                    el.play();
                  } else {
                    el.pause();
                  }
                };
              }
            }}
            src={currentImage.file_path}
            controls
            className="max-w-full object-contain cursor-pointer"
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          />
        ) : (
          <img
            ref={imageRef}
            src={currentImage.file_path}
            alt={`Generated image from ${currentImage.job_name}`}
            className="max-w-full max-h-full object-contain transition-transform duration-100"
            style={{
              transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
              transformOrigin: 'center center'
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={handleDoubleClick}
            onDragStart={(e) => e.preventDefault()} // Prevent default image drag
          />
        )}

        {/* Navigation arrows */}
        {currentIndex > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-all"
            title="Previous image (← arrow key)"
          >
            ←
          </button>
        )}

        {currentIndex < images.length - 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-all"
            title="Next image (→ arrow key)"
          >
            →
          </button>
        )}

        {/* Folder hint notification */}
        {showFolderHint && (
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white px-4 py-2 rounded-lg font-medium pointer-events-none">
            Moved to folder!
          </div>
        )}

        {/* Top toolbar */}
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center pointer-events-none">
          <div className="flex items-center space-x-3">
            <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded">
              {currentIndex + 1} of {images.length}
            </div>
            {currentImage.folder_path && (
              <div className="flex items-center space-x-2">
                <div className="bg-blue-500 text-white px-3 py-1 rounded text-sm">
                  📁 {currentImage.folder_path}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromFolder();
                  }}
                  className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 transition-colors pointer-events-auto"
                  title="Remove from folder (U key)"
                >
                  Remove
                </button>
              </div>
            )}
            {/* Zoom indicator */}
            {scale !== 1 && (
              <div className="bg-blue-500 text-white px-3 py-1 rounded text-sm">
                {Math.round(scale * 100)}%
              </div>
            )}
          </div>

          <div className="flex space-x-2 pointer-events-auto">
            {scale !== 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  resetZoom();
                }}
                className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors text-sm"
                title="Reset zoom (0 key or double-click)"
              >
                Reset Zoom
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleArchive();
              }}
              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
              title="Archive image (Delete key)"
            >
              Archive
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all"
              title="Close (Escape key)"
            >
              ×
            </button>
          </div>
        </div>

        {/* Folder hotkeys sidebar */}
        {folders.length > 0 && (
          <div className="absolute right-4 top-20 bg-black bg-opacity-75 text-white p-3 rounded-lg max-w-xs pointer-events-none">
            <h4 className="text-sm font-medium mb-2">Quick Move to Folder:</h4>
            <div className="space-y-1 text-xs">
              {folders.slice(0, 8).map(folder => (
                <div key={folder.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: folder.color }}
                    />
                    <span className="truncate max-w-24">{folder.name}</span>
                  </div>
                  {folder.hotkey && (
                    <kbd className="bg-gray-700 px-1 py-0.5 rounded text-xs font-mono">
                      {folder.hotkey}
                    </kbd>
                  )}
                </div>
              ))}
              {folders.length > 8 && (
                <div className="text-gray-400 text-xs">+{folders.length - 8} more...</div>
              )}
            </div>
          </div>
        )}

        {/* Bottom info panel */}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-4 pointer-events-none">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-lg">{currentImage.job_name}</h3>
                <p className="text-sm text-gray-300">{currentImage.workflow_name}</p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <div className="flex items-center space-x-4">
                    <span>{new Date(currentImage.created_at).toLocaleDateString()}</span>
                    <span>Seed: {currentImage.seed_used}</span>
                  </div>
                  {currentImage.user_tags && currentImage.user_tags.length > 0 && (
                    <div className="flex space-x-1">
                      {currentImage.user_tags.map((tag, index) => (
                        <span key={index} className="bg-gray-600 px-2 py-1 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/job/${currentImage.job_id}`);
                }}
                className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors pointer-events-auto flex items-center space-x-2"
                title="View Job Details"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>View Job</span>
              </button>
            </div>

            {/* Keyboard shortcuts help */}
            <div className="mt-3 text-xs text-gray-500 border-t border-gray-600 pt-2">
              <span className="mr-4">← → Navigate</span>
              {isVideo(currentImage.file_path) ? (
                <span className="mr-4">Click video to Play/Pause</span>
              ) : (
                <>
                  <span className="mr-4">Mouse Wheel Zoom</span>
                  <span className="mr-4">Drag to Pan</span>
                  <span className="mr-4">Double-click Reset</span>
                  <span className="mr-4">0 Reset Zoom</span>
                </>
              )}
              <span className="mr-4">Delete Archive</span>
              {Object.keys(folderMappings).length > 0 && (
                <span className="mr-4">{Object.keys(folderMappings).join(', ')} Move to Folder</span>
              )}
              <span className="mr-4">U Remove from Folder</span>
              <span className="mr-4">Esc Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageLightbox;
